"use client"

import * as React from "react"
import { type FC, type ReactNode } from "react"

type ViewTransitionClass =
  | "auto"
  | "none"
  | (string & {})
  | Record<string, "auto" | "none" | (string & {})>

type ViewTransitionProps = {
  children?: ReactNode
  default?: ViewTransitionClass
  enter?: ViewTransitionClass
  exit?: ViewTransitionClass
  name?: string
  share?: ViewTransitionClass
  update?: ViewTransitionClass
}

// React 19's `<ViewTransition>` is currently shipped under the
// canary `ViewTransition` export in Next.js, while some builds still expose
// `unstable_ViewTransition`. Pull it off the namespace at runtime and assert
// the prop shape we use.
const ReactViewTransition = React as unknown as {
  ViewTransition?: React.ComponentType<ViewTransitionProps>
  unstable_ViewTransition?: React.ComponentType<ViewTransitionProps>
}

const ViewTransition =
  ReactViewTransition.ViewTransition ??
  ReactViewTransition.unstable_ViewTransition

interface InnerProps {
  /**
   * Optional stable name for shared-element transitions. Page-level motion is
   * class driven so Suspense reveals can use a different animation.
   */
  name?: string
  default?: ViewTransitionClass
  enter?: ViewTransitionClass
  exit?: ViewTransitionClass
  share?: ViewTransitionClass
  update?: ViewTransitionClass
  children: ReactNode
}

/**
 * Thin wrapper around React's experimental `<ViewTransition>` component
 * enabled by Next.js. Callers decide whether they need a stable shared-element
 * `name`, transition-type maps, or a silent passthrough.
 */
const Inner: FC<InnerProps> = ({
  children,
  default: defaultAnimation,
  enter,
  exit,
  name,
  share,
  update,
}) => {
  // If the experimental component isn't available (e.g. SSR with an older
  // React build), render children passthrough so the app still works.
  if (!ViewTransition) {
    return <>{children}</>
  }

  return (
    <ViewTransition
      default={defaultAnimation}
      enter={enter}
      exit={exit}
      name={name}
      share={share}
      update={update}
    >
      {children}
    </ViewTransition>
  )
}

export default Inner
